import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../rbc-overrides.css';
import Cookies from 'js-cookie';

import AddIcon from '@mui/icons-material/Add';
import 'moment/locale/ja';

moment.locale('ja');
const localizer = momentLocalizer(moment);

const FACILITIES_API = 'https://system.sales-management.cloud/api/facilities.php';

const messages = {
  allDay: '終日',
  previous: '前',
  next: '次',
  today: '今日',
  month: '月',
  week: '週',
  day: '日',
  agenda: '予約リスト',
  date: '日付',
  time: '時間',
  event: '予約',
  noEventsInRange: 'この期間に予約はありません',
  showMore: (total) => `+ ${total} 件`
};

// 施設の色マッピング
const facilityColorPalette = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

function Institution() {
  const [reservations, setReservations] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [facilityColors, setFacilityColors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedFacility, setSelectedFacility] = useState('');
  const [view, setView] = useState('week');

  // フォームデータ
  const [formData, setFormData] = useState({
    facility_id: '',
    start_datetime: '',
    end_datetime: '',
    purpose: '',
    notes: ''
  });

  // 予約一覧を取得
  const fetchReservations = useCallback(async (date = currentDate) => {
    setLoading(true);
    setError('');
    try {
      const token = Cookies.get('auth_token');
      if (!token) {
        setError('認証情報が見つかりません');
        return;
      }

      const startOfMonth = moment(date).startOf('month').format('YYYY-MM-DD');
      const endOfMonth = moment(date).endOf('month').format('YYYY-MM-DD');

      let url = `${FACILITIES_API}?start_date=${startOfMonth}&end_date=${endOfMonth}`;
      if (selectedFacility) {
        url += `&facility_id=${selectedFacility}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      

      if (data.status === 'success') {
        // 施設情報を取得して色を割り当て
        const facilitiesList = data.facilities || [];
        if (facilitiesList.length > 0) {
          setFacilities(facilitiesList);
          
          // 施設IDに色を割り当て（APIから色が来ている場合はそれを使用）
          const colors = {};
          facilitiesList.forEach((facility, index) => {
            colors[facility.id] = facility.color || facilityColorPalette[index % facilityColorPalette.length];
          });
          setFacilityColors(colors);
        }

        // 新しいAPIレスポンス構造: data.reservations.data
        const reservationList = data.reservations?.data || [];

        const formattedReservations = reservationList.map(reservation => ({
          ...reservation,
          id: reservation.id,
          title: `${reservation.facility_name || '施設'} - ${reservation.user_name || reservation.owner_name || reservation.creator_name || '予約者'}`,
          start: new Date(reservation.start_datetime.replace(' ', 'T')),
          end: new Date(reservation.end_datetime.replace(' ', 'T')),
          facility_id: reservation.facility_id,
          facility_name: reservation.facility_name
        }));
        
        setReservations(formattedReservations);
      } else {
        setError(data.message || '予約の取得に失敗しました');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('通信エラーが発生しました');
    } finally {
      setLoading(false);
    }
  }, [currentDate, selectedFacility]);

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  // 予約の作成
  const handleCreate = async () => {
    if (!formData.facility_id || !formData.start_datetime || !formData.end_datetime) {
      setError('施設、開始日時、終了日時は必須です');
      return;
    }

    setModalLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = Cookies.get('auth_token');
      if (!token) {
        setError('認証情報が見つかりません');
        return;
      }

      const requestBody = {
        facility_id: parseInt(formData.facility_id),
        start_datetime: formData.start_datetime.replace('T', ' ') + ':00',
        end_datetime: formData.end_datetime.replace('T', ' ') + ':00'
      };

      if (formData.purpose) requestBody.purpose = formData.purpose;
      if (formData.notes) requestBody.notes = formData.notes;

      const response = await fetch(FACILITIES_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      

      if (data.status === 'success') {
        setSuccess('予約を作成しました');
        setIsModalOpen(false);
        resetForm();
        fetchReservations();
      } else {
        setError(data.message || '予約の作成に失敗しました');
      }
    } catch (err) {
      console.error('Create error:', err);
      setError('通信エラーが発生しました');
    } finally {
      setModalLoading(false);
    }
  };

  // 予約の更新
  const handleUpdate = async () => {
    if (!selectedReservation?.id) return;

    setModalLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = Cookies.get('auth_token');
      if (!token) {
        setError('認証情報が見つかりません');
        return;
      }

      const requestBody = {
        id: selectedReservation.id
      };

      if (formData.facility_id) requestBody.facility_id = parseInt(formData.facility_id);
      if (formData.start_datetime) requestBody.start_datetime = formData.start_datetime.replace('T', ' ') + ':00';
      if (formData.end_datetime) requestBody.end_datetime = formData.end_datetime.replace('T', ' ') + ':00';
      if (formData.purpose) requestBody.purpose = formData.purpose;
      if (formData.notes) requestBody.notes = formData.notes;

      const response = await fetch(FACILITIES_API, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      

      if (data.status === 'success') {
        setSuccess('予約を更新しました');
        setIsModalOpen(false);
        resetForm();
        setSelectedReservation(null);
        fetchReservations();
      } else {
        setError(data.message || '予約の更新に失敗しました');
      }
    } catch (err) {
      console.error('Update error:', err);
      setError('通信エラーが発生しました');
    } finally {
      setModalLoading(false);
    }
  };

  // 予約のキャンセル
  const handleDelete = async () => {
    if (!selectedReservation?.id) return;

    if (!window.confirm('この予約をキャンセルしますか?')) return;

    setModalLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = Cookies.get('auth_token');
      if (!token) {
        setError('認証情報が見つかりません');
        return;
      }

      const response = await fetch(`${FACILITIES_API}?id=${selectedReservation.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      

      if (data.status === 'success') {
        setSuccess('予約をキャンセルしました');
        setIsModalOpen(false);
        resetForm();
        setSelectedReservation(null);
        fetchReservations();
      } else {
        setError(data.message || '予約のキャンセルに失敗しました');
      }
    } catch (err) {
      console.error('Delete error:', err);
      setError('通信エラーが発生しました');
    } finally {
      setModalLoading(false);
    }
  };

  const handleSelectEvent = (event) => {
    setSelectedReservation(event);
    setFormData({
      facility_id: event.facility_id.toString(),
      start_datetime: moment(event.start).format('YYYY-MM-DDTHH:mm'),
      end_datetime: moment(event.end).format('YYYY-MM-DDTHH:mm'),
      purpose: event.purpose || '',
      notes: event.notes || ''
    });
    setIsModalOpen(true);
  };

  const handleSelectSlot = (slotInfo) => {
    setSelectedReservation(null);
    setFormData({
      facility_id: '',
      start_datetime: moment(slotInfo.start).format('YYYY-MM-DDTHH:mm'),
      end_datetime: moment(slotInfo.end).format('YYYY-MM-DDTHH:mm'),
      purpose: '',
      notes: ''
    });
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      facility_id: '',
      start_datetime: '',
      end_datetime: '',
      purpose: '',
      notes: ''
    });
    setSelectedReservation(null);
  };

  const handleNavigate = (date) => {
    setCurrentDate(date);
    fetchReservations(date);
  };

  const handleViewChange = (newView) => {
    setView(newView);
  };

  const eventStyleGetter = (event) => {
    const color = facilityColors[event.facility_id] || '#6366f1';
    return {
      style: {
        backgroundColor: color,
        borderRadius: '6px',
        opacity: 0.95,
        color: 'white',
        border: '0px',
        display: 'block',
        fontSize: '12px',
        lineHeight: 1.3,
        padding: '2px 6px',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden'
      }
    };
  };

  return (
    <div className="max-w-7xl mx-auto p-0 sm:p-4 md:p-6">
      <div className="flex justify-between items-center mb-4 md:mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">施設予約</h1>
        <button
          onClick={() => {
            resetForm();
            setIsModalOpen(true);
          }}
          className="flex items-center gap-2 px-3 md:px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm md:text-base"
        >
          <AddIcon />
          新規予約
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
          <p className="text-sm text-green-600">{success}</p>
        </div>
      )}

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          施設でフィルタ
        </label>
        <select
          value={selectedFacility}
          onChange={(e) => setSelectedFacility(e.target.value)}
          className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">すべての施設</option>
          {facilities.map(facility => (
            <option key={facility.id} value={facility.id}>
              {facility.name}
            </option>
          ))}
        </select>
      </div>

      <div className="bg-white rounded-lg shadow-sm md:shadow-md p-3 md:p-6 mx-0 sm:mx-0 md:mx-0" style={{ height: '700px' }}>
        {loading ? (
          <div className="h-full flex items-center justify-center text-gray-500">
            読み込み中...
          </div>
        ) : (
          <Calendar
            localizer={localizer}
            events={reservations}
            startAccessor="start"
            endAccessor="end"
            titleAccessor="title"
            style={{ height: '100%' }}
            onSelectEvent={handleSelectEvent}
            onSelectSlot={handleSelectSlot}
            selectable
            messages={messages}
            formats={{
              agendaHeaderFormat: ({ start, end }) => `${moment(start).format('YYYY年M月D日')} – ${moment(end).format('YYYY年M月D日')}`,
              agendaDateFormat: (date) => moment(date).format('M/D(ddd)'),
              agendaTimeFormat: (date) => moment(date).format('HH:mm'),
              eventTimeRangeFormat: ({ start, end }) => `${moment(start).format('HH:mm')}–${moment(end).format('HH:mm')}`
            }}
            eventPropGetter={eventStyleGetter}
            onNavigate={handleNavigate}
            onView={handleViewChange}
            date={currentDate}
            view={view}
            views={['month', 'week', 'day', 'agenda']}
            min={new Date(1970, 1, 1, 6, 0, 0)}
            max={new Date(1970, 1, 1, 23, 59, 59)}
            scrollToTime={new Date(1970, 1, 1, 6, 0, 0)}
          />
        )}
      </div>

      <div className="mt-3 md:mt-4 flex gap-3 md:gap-4 text-xs md:text-sm flex-wrap">
        {facilities.map(facility => (
          <div key={facility.id} className="flex items-center gap-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: facilityColors[facility.id] }}></div>
            <span>{facility.name}</span>
          </div>
        ))}
      </div>

      {/* モーダル */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {selectedReservation ? '予約編集' : '新規予約'}
                </h2>
                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    施設 <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.facility_id}
                    onChange={(e) => setFormData({ ...formData, facility_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">施設を選択</option>
                    {facilities.map(facility => (
                      <option key={facility.id} value={facility.id}>
                        {facility.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    開始日時 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.start_datetime}
                    onChange={(e) => setFormData({ ...formData, start_datetime: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    終了日時 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.end_datetime}
                    onChange={(e) => setFormData({ ...formData, end_datetime: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    利用目的
                  </label>
                  <input
                    type="text"
                    value={formData.purpose}
                    onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="会議、研修など"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    備考
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="その他メモ"
                  />
                </div>
              </div>

              <div className="flex justify-between items-center mt-6 pt-4 border-t">
                {selectedReservation && (
                  <button
                    onClick={handleDelete}
                    disabled={modalLoading}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    {modalLoading ? '処理中...' : 'キャンセル'}
                  </button>
                )}
                <div className={`flex gap-2 ${!selectedReservation ? 'ml-auto' : ''}`}>
                  <button
                    onClick={() => {
                      setIsModalOpen(false);
                      resetForm();
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                  >
                    閉じる
                  </button>
                  <button
                    onClick={selectedReservation ? handleUpdate : handleCreate}
                    disabled={modalLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    {modalLoading ? '処理中...' : selectedReservation ? '更新' : '作成'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Institution;